import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiRobotOutline, mdiAlertCircleOutline, mdiCheckCircleOutline } from '@mdi/js'
import { EmptyState } from '../ui/empty-state'
import { useJobs, type JobExecutionResponse } from '../../api/jobs'

interface ManifestEntry {
    asset_uid: string
    asset_type: string
    identifiers?: Record<string, unknown>
    flags?: string[]
}

interface BuildingSummary {
    min_level?: number | null
    max_level?: number | null
    total_floor_count?: number | null
    has_basement?: boolean | null
    has_attic?: boolean | null
    has_elevator?: boolean | null
    elevator_count?: number | null
}

interface ExtractionManifest {
    doc_kind?: string
    is_generating: boolean
    building_summary?: BuildingSummary | null
    created: ManifestEntry[]
    updated: ManifestEntry[]
    subject_asset_uid?: string | null
    subject_fallback?: boolean
    subject_identifiers?: Record<string, unknown> | null
    to_approve_count: number
    prompt_file?: string
    prompt_fingerprint?: string
    rooms_deferred?: number
    conflicts?: ManifestConflict[]
}

interface ManifestConflict {
    asset_uid: string
    property: string
    current: string
    proposed: string
}

function parseManifest(exec: JobExecutionResponse | undefined): ExtractionManifest | null {
    if (!exec?.output) return null
    // JobExecution.Output is stored as JSONB; the hook gives us a Record.
    return exec.output as unknown as ExtractionManifest
}

function identifiersSummary(ids?: Record<string, unknown>): string {
    if (!ids) return ''
    const parts: string[] = []
    for (const [k, v] of Object.entries(ids)) {
        if (v === null || v === undefined || v === '') continue
        parts.push(`${k}=${String(v)}`)
    }
    return parts.slice(0, 4).join(', ')
}

export interface ExtractionManifestSummaryProps {
    org: string
    documentUid: string
}

/**
 * Renders the summary of the latest doc_asset_extraction run for a document:
 * AI-created/updated assets, building-summary card, pending-review count,
 * and a subject_fallback hint.
 *
 * Silently renders nothing when no `doc_asset_extraction` job exists for the
 * document (e.g. documents whose type has no `asset_declaration`).
 */
export function ExtractionManifestSummary({ org, documentUid }: ExtractionManifestSummaryProps) {
    const { t } = useTranslation('documents')

    const { data, isLoading } = useJobs(org, {
        type: 'doc_asset_extraction',
        target_entity_type: 'document',
        target_entity_uid: documentUid,
        limit: 1,
    })

    const job = data?.jobs?.[0]
    const execution = job?.last_execution
    const manifest = useMemo(() => parseManifest(execution), [execution])

    if (isLoading) return null
    if (!job) return null

    if (execution && (execution.status === 'pending' || execution.status === 'running')) {
        return (
            <div className="mt-6 bg-card border border-border rounded-lg p-4" data-testid="extraction-manifest-pending">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <AppIcon path={mdiRobotOutline} size="md" />
                    <span>{t('extraction_manifest.status_pending')}</span>
                </div>
            </div>
        )
    }

    if (execution && execution.status === 'failed') {
        return (
            <div className="mt-6 bg-card border border-destructive/40 rounded-lg p-4" data-testid="extraction-manifest-failed">
                <div className="flex items-center gap-2 text-destructive text-sm">
                    <AppIcon path={mdiAlertCircleOutline} size="md" />
                    <span>{t('extraction_manifest.status_failed')}</span>
                </div>
            </div>
        )
    }

    if (!manifest) {
        return (
            <EmptyState
                className="mt-6"
                icon={mdiRobotOutline}
                title={t('extraction_manifest.empty')}
                data-testid="extraction-manifest-empty"
            />
        )
    }

    const created = manifest.created ?? []
    const updated = manifest.updated ?? []
    const bs = manifest.building_summary
    const subjectIds = manifest.subject_identifiers
    const subjectFallback = manifest.subject_fallback ?? false
    const roomsDeferred = manifest.rooms_deferred ?? 0
    const conflicts = manifest.conflicts ?? []

    return (
        <div className="mt-6 bg-card border border-border rounded-lg" data-testid="extraction-manifest-summary">
            <div className="p-4 border-b border-border flex items-center gap-2">
                <AppIcon path={mdiRobotOutline} size="md" className="text-primary" />
                <h3 className="font-medium">{t('extraction_manifest.title')}</h3>
                {manifest.to_approve_count > 0 && (
                    <span
                        className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        data-testid="extraction-manifest-to-approve-count"
                    >
                        {t('extraction_manifest.to_approve_count', { count: manifest.to_approve_count })}
                    </span>
                )}
            </div>

            {!manifest.is_generating && created.length === 0 && updated.length === 0 && (
                <EmptyState
                    variant="inline"
                    title={t('extraction_manifest.not_generating')}
                    data-testid="extraction-manifest-not-generating"
                />
            )}

            {bs && (bs.min_level != null || bs.max_level != null || bs.total_floor_count != null || bs.has_elevator != null) && (
                <div className="p-4 border-b border-border">
                    <div className="text-xs uppercase text-muted-foreground mb-2">
                        {t('extraction_manifest.building_summary')}
                    </div>
                    <div className="text-sm text-foreground flex flex-wrap gap-x-4 gap-y-1">
                        {bs.total_floor_count != null && <span>{bs.total_floor_count} floors</span>}
                        {bs.min_level != null && <span>min {bs.min_level}</span>}
                        {bs.max_level != null && <span>max {bs.max_level}</span>}
                        {bs.has_basement === true && <span>basement</span>}
                        {bs.has_attic === true && <span>attic</span>}
                        {bs.has_elevator === true && (
                            <span>
                                {bs.elevator_count != null ? `${bs.elevator_count} elevator(s)` : 'elevator'}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {subjectFallback && subjectIds && Object.keys(subjectIds).length > 0 && (
                <div className="p-4 border-b border-border text-sm text-muted-foreground" data-testid="extraction-manifest-fallback-hint">
                    <AppIcon path={mdiAlertCircleOutline} size="sm" className="inline mr-1" />
                    {t('extraction_manifest.subject_fallback_hint', { ids: identifiersSummary(subjectIds) })}
                </div>
            )}

            {(created.length > 0 || updated.length > 0) && (
                <div className="divide-y divide-border">
                    {created.length > 0 && (
                        <ManifestSection
                            title={t('extraction_manifest.created')}
                            entries={created}
                            org={org}
                        />
                    )}
                    {updated.length > 0 && (
                        <ManifestSection
                            title={t('extraction_manifest.updated')}
                            entries={updated}
                            org={org}
                        />
                    )}
                </div>
            )}

            {conflicts.length > 0 && (
                <div className="p-4 border-t border-border" data-testid="extraction-manifest-conflicts">
                    <div className="text-xs uppercase text-muted-foreground mb-2 flex items-center gap-1">
                        <AppIcon path={mdiAlertCircleOutline} size="xs" />
                        <span>{t('extraction_manifest.conflicts', { count: conflicts.length })}</span>
                    </div>
                    <ul className="space-y-1 text-sm">
                        {conflicts.map((c) => (
                            <li
                                key={`${c.asset_uid}-${c.property}-${c.proposed}`}
                                className="flex flex-wrap items-center gap-2"
                                data-testid={`extraction-manifest-conflict-${c.asset_uid}-${c.property}`}
                            >
                                <Link
                                    to="/$org/assets/$assetUid"
                                    params={{ org, assetUid: c.asset_uid }}
                                    className="text-foreground hover:underline"
                                    data-testid={`extraction-manifest-conflict-link-${c.asset_uid}`}
                                >
                                    {c.property}
                                </Link>
                                <span className="text-muted-foreground">
                                    {t('extraction_manifest.conflict_values', { current: c.current, proposed: c.proposed })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {roomsDeferred > 0 && (
                <div className="p-4 text-xs text-muted-foreground" data-testid="extraction-manifest-rooms-deferred">
                    {t('extraction_manifest.rooms_deferred', { count: roomsDeferred })}
                </div>
            )}
        </div>
    )
}

function ManifestSection({ title, entries, org }: { title: string; entries: ManifestEntry[]; org: string }) {
    return (
        <div className="p-4" data-testid={`extraction-manifest-section-${title.toLowerCase()}`}>
            <div className="text-xs uppercase text-muted-foreground mb-2 flex items-center gap-1">
                <AppIcon path={mdiCheckCircleOutline} size="xs" />
                <span>
                    {title} ({entries.length})
                </span>
            </div>
            <ul className="space-y-1 text-sm">
                {entries.map((e) => (
                    <li key={e.asset_uid} className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                            {e.asset_type}
                        </span>
                        <Link
                            to="/$org/assets/$assetUid"
                            params={{ org, assetUid: e.asset_uid }}
                            className="text-foreground hover:underline"
                            data-testid={`extraction-manifest-entry-${e.asset_uid}`}
                        >
                            {identifiersSummary(e.identifiers) || e.asset_uid.slice(0, 8)}
                        </Link>
                        {e.flags?.includes('to_approve') && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300">
                                to_approve
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    )
}
